<script setup lang="ts">
import { ref, onBeforeMount, reactive, computed, onUnmounted } from 'vue';
import i18n from '@/i18n';
import { getEnvVar } from '@/utils/env';
import { usePromosStore } from '@/store/PromosStore';
import { useComponentComposable } from '@/components/useComponentComposable';
import Button from 'primevue/button';
import AccordionPromo from '@/components/promos/AccordionPromo.vue';
import AppTopbar from '@/layout/AppTopbar.vue';
import BottomBannerPreview from '@/components/admin/HomePromoAdmin/BottomBannerPreview.vue';
import CardPromo from '@/components/promos/CardPromo.vue';
import ShowImageByScreen from '@/components/ShowImageByScreen.vue';
import SurfacePromo from '@/components/promos/SurfacePromo.vue';

const environment = getEnvVar('VITE_SITE');
const hybrisUrl = getEnvVar('VITE_JWT_ISS');
const { renderHTMLContent } = useComponentComposable();
const promosStore = usePromosStore();

const listAccordionPromos = ref([]);
const surfacePromoInfo = ref([]);
const bindingPromoBox = ref([]);
const previewDataTopBanner = ref({});
const previewDataImageFooter = ref();
const lang = i18n.global.locale.value.replace(/^\w/, (c) => c.toUpperCase());
const currentLanguage = i18n.global.locale.value;
const sectionTitle = reactive({});
const linkGroupForm = currentLanguage === 'fr' ? new URL('fr/formulaire-de-groupe', `${hybrisUrl}`) : new URL(`en/group-form`, `${hybrisUrl}`);

onBeforeMount(() => {
  promosStore.getSurfacePromos().then((res) => {
    if (!res?.[0]?.['showInB2c' + lang]) {
      return;
    }
    surfacePromoInfo.value = res;
  });

  promosStore.getAccordionPromos().then((res) => {
    const accordionArray = [];
    res
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        if (!item.showInB2cEn && !item.showInB2cFr) {
          return;
        }
        if (!item['showInB2c' + lang]) {
          return;
        }
        const promoBoxItem = {
          title: item['title' + lang],
          subTitle: item['subTitle' + lang],
          description: item['desc' + lang],
          renderImage: item['image' + lang],
          terms: item['terms' + lang]
        };
        accordionArray.push(promoBoxItem);
      });
    listAccordionPromos.value = accordionArray;
  });

  promosStore.getPromoBox().then((res) => {
    const promoBoxArray = [];

    res.sort((a, b) => a.order - b.order);
    res.map((item) => {
      if (!item.showInB2cEn && !item.showInB2cFr) {
        return;
      }
      if (!item['showInB2c' + lang]) {
        return;
      }
      const promoBoxItem = {
        title: item['title' + lang],
        description: item['desc' + lang],
        image: item['imgUrl' + lang],
        imageList: item['image' + lang],
        terms: item['terms' + lang],
        isShow: item['showInB2b' + lang],
        color: currentLanguage === 'en' ? item.colorEn : item.colorFr
      };
      promoBoxArray.push(promoBoxItem);
    });
    bindingPromoBox.value = promoBoxArray;
  });

  promosStore.getTopBannerHomePageInfo().then((res) => {
    previewDataTopBanner.value = res?.['hPage' + lang];
    const metaTags = [
      {
        description: previewDataTopBanner.value?.metaDescription
      },
      {
        keywords: previewDataTopBanner.value?.metaKeyword
      }
    ];
    setMetaTag(metaTags, previewDataTopBanner.value?.metaTitle);
  });

  promosStore.getBottomBannerHomePageInfo().then((res) => {
    previewDataImageFooter.value = res?.['hPage' + lang];
  });
  getSectionTitlePromo();
});

const setMetaTag = (metaTags, title = 'GQT') => {
  Array.from(document.querySelectorAll('[data-vue-component-controlled]')).forEach((el) => el.parentNode.removeChild(el));
  document.title = title;
  metaTags
    ?.map((tagDef) => {
      const tag = document.createElement('meta');
      Object.keys(tagDef).forEach((key) => {
        tag.setAttribute('name', key);
        tag.setAttribute('content', tagDef[key]);
      });
      tag.setAttribute('data-vue-component-controlled', '');
      return tag;
    })
    .forEach((tag) => document.head.appendChild(tag));
};

const getSectionTitlePromo = () => {
  promosStore.getSectionTitlePromo().then((res) => {
    sectionTitle.promoBoxTitleEn = res.promoBoxTitleEn;
    sectionTitle.promoBoxTitleFr = res.promoBoxTitleFr;
    sectionTitle.accordionPromoTitleFr = res.accordionPromoTitleFr;
    sectionTitle.accordionPromoTitleEn = res.accordionPromoTitleEn;
    sectionTitle.surfacePromoTitleFr = res.surfacePromoTitleFr;
    sectionTitle.surfacePromoTitleEn = res.surfacePromoTitleEn;
  });
};

const bindingSurfacePromo = computed(() => ({
  name: surfacePromoInfo.value[0]['title' + lang],
  subTitle: surfacePromoInfo.value[0]['subTitle' + lang],
  description: surfacePromoInfo.value[0]['desc' + lang],
  image: surfacePromoInfo.value[0]['imgUrl' + lang],
  term: surfacePromoInfo.value[0]['terms' + lang],
  imageTitle: surfacePromoInfo.value[0]['imgTitle' + lang],
  nameFirstButton: surfacePromoInfo.value[0]['nameFirstButton' + lang],
  urlFirstButton: surfacePromoInfo.value[0]['urlFirstButton' + lang],
  nameSecondButton: surfacePromoInfo.value[0]['nameSecondButton' + lang],
  urlSecondButton: surfacePromoInfo.value[0]['urlSecondButton' + lang],
  renderImage: surfacePromoInfo.value[0]['image' + lang]
}));

const handleRedirect = (url: string) => {
  if (!url) {
    return;
  }
  window.open(url, '_blank');
};

const clearMetaTag = () => {
  document.title = 'GQT';
  const metaElements = document.querySelectorAll('[data-vue-component-controlled]');
  metaElements.forEach((el) => el.parentNode.removeChild(el));
};

onUnmounted(() => {
  clearMetaTag();
});
</script>

<template>
  <AppTopbar v-if="environment === 'DEV'" :isLogged="false" />
  <div :class="environment === 'DEV'" @click="handleRedirect(previewDataTopBanner.urlLink)">
    <div v-if="previewDataTopBanner" class="surface-section text-800 mb-5 top-banner-container">
      <div class="overflow-hidden w-full h-full">
        <ShowImageByScreen alt="image-render" class="h-full w-full md:ml-auto block object-fit-cover" :images="previewDataTopBanner?.image"></ShowImageByScreen>
      </div>
    </div>
  </div>
  <div class="container-outer mb-6">
    <div class="justify-content-center w-full">
      <div class="relative">
        <div class="banner-title">
          <section>
            <renderHTMLContent class="mb-3" :data="previewDataTopBanner?.title" htmlElement="h1"></renderHTMLContent>
            <renderHTMLContent class="sub-title" :data="previewDataTopBanner?.subTitle" htmlElement="p"></renderHTMLContent>
          </section>
        </div>
        <p class="landing-intro">{{ $t('landing_page.intro') }}</p>
        <div class="flex align-items-center flex-wrap gap-2">
          <Button class="vacv-btn" @click="handleRedirect(linkGroupForm.toString())">{{ $t('landing_page.get_group') }}</Button>
        </div>
        <p class="mt-4">{{ $t('landing_page.des_support_sub') }} 1-866-529-2079.</p>
        <renderHTMLContent class="mt-5" :data="i18n.global.locale.value === 'fr' ? sectionTitle?.promoBoxTitleFr : sectionTitle?.promoBoxTitleEn" htmlElement="h2"></renderHTMLContent>
        <div class="promo-listing-wrapper">
          <div class="promo-listing" v-if="bindingPromoBox.length">
            <div class="static-tile tile-big" v-for="(promoBox, index) in bindingPromoBox" :key="index">
              <CardPromo :valuePromoCard="promoBox" />
            </div>
          </div>
        </div>
        <div class="mt-5" v-if="surfacePromoInfo.length">
          <renderHTMLContent v-if="sectionTitle.length" :data="i18n.global.locale.value === 'fr' ? sectionTitle?.surfacePromoTitleFr : sectionTitle?.surfacePromoTitleEn" htmlElement="h2"></renderHTMLContent>
          <SurfacePromo :value="bindingSurfacePromo" />
        </div>

        <div class="grid my-4 ml-0" v-if="!!listAccordionPromos.length">
          <renderHTMLContent v-if="sectionTitle.length" :data="i18n.global.locale.value === 'fr' ? sectionTitle?.accordionPromoTitleFr : sectionTitle?.accordionPromoTitleEn" htmlElement="h2"></renderHTMLContent>
          <div class="col-12 pl-0" v-for="(accordion, index) in listAccordionPromos" :key="index">
            <AccordionPromo :showTerm="true" :value="accordion" :maxContentCharacters="1000000" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="cursor-pointer" @click="handleRedirect(previewDataImageFooter.urlLink)">
    <BottomBannerPreview :value="previewDataImageFooter"></BottomBannerPreview>
  </div>
</template>
